import { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { SubscriptionContext } from '../../contexts/SubscriptionContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useMetaPixel } from '../../contexts/MetaPixelContext';
import subscriptionService from '../../services/subscriptionService';
import authService from '../../services/authService';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import { Helmet } from 'react-helmet-async';
import { FaCrown, FaCheck, FaArrowRight, FaInfinity, FaCertificate, FaChartLine, FaStar, FaLock, FaUser, FaEnvelope, FaKey } from 'react-icons/fa';
import './style.css';

const SubscribePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useContext(AuthContext);
  const subscriptionContext = useContext(SubscriptionContext);
  const { trackSignup, trackButtonClick, trackUpgradeButtonClicked, trackError } = useAnalytics();
  const { trackCustom } = useMetaPixel();

  // Safety check and logging
  console.log('SubscriptionContext:', subscriptionContext);

  const {
    subscription,
    hasActiveSubscription = false,
    isPaidSubscription = false
  } = subscriptionContext || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('year'); // 'month' or 'year'

  // States for simplified enrollment form
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: ''
  });

  const SUBSCRIPTION_PRICE_YEAR = 49;
  const SUBSCRIPTION_PRICE_MONTH = 4.9;
  const OLD_PRICE_YEAR = 59; // Hypothetical old price for comparison

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }

    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    // Password strength validation
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    return true;
  };

  const handleSimplifiedEnroll = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create user account through simplified enrollment
      // Generate a valid username (only letters, numbers, and underscores)
      let cleanFirstName = formData.first_name.trim().replace(/[^a-zA-Z0-9_]/g, '');
      let cleanLastName = formData.last_name.trim().replace(/[^a-zA-Z0-9_]/g, '');

      // Ensure we have valid non-empty parts for username
      if (!cleanFirstName) cleanFirstName = 'user';
      if (!cleanLastName) cleanLastName = 'learner';

      const timestamp = Date.now();
      const username = `${cleanFirstName}_${cleanLastName}_${timestamp}`.toLowerCase();

      const userData = {
        email: formData.email.trim(),
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        username: username
      };

      // Use the register method for simplified enrollment
      const response = await authService.register(userData);
      console.log('Registration response:', response);

      // Check if we received the expected response structure
      if (!response || !response.user || !response.access_token) {
        throw new Error('Invalid response from server');
      }

      // Track successful signup from subscribe page
      trackSignup(
        'email',
        response.user.id,
        response.user.email,
        response.user.first_name,
        response.user.last_name
      );

      // Automatically log in the user
      updateUser(response.user);

      // Store tokens in localStorage
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Show success message
      setError('Account created successfully! Redirecting to payment...');

      // Small delay before proceeding to payment
      setTimeout(() => {
        // Now that the user is registered and logged in, proceed to payment
        proceedToPayment();
      }, 1500);

    } catch (err) {
      console.error('Registration error:', err);
      // Extract error message from various possible response structures
      let errorMessage = 'Failed to create account. Please try again.';

      if (err.response?.data?.detail?.message) {
        errorMessage = err.response.data.detail.message;
      } else if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.detail) {
        // Handle array of errors
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail[0]?.msg || errorMessage;
        } else {
          errorMessage = err.response.data.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Track registration error
      trackError('subscribe_registration_failed', errorMessage, {
        email: formData.email,
        selected_plan: selectedPlan,
      });

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    try {
      setLoading(true);
      setError(null);

      const checkoutData = {
        amount: selectedPlan === 'year' ? SUBSCRIPTION_PRICE_YEAR : SUBSCRIPTION_PRICE_MONTH,
        currency: 'EUR',
        interval: selectedPlan,
        success_url: `${window.location.origin}/payment-success`,
        cancel_url: `${window.location.origin}/subscribe`
      };

      // Track upgrade button click
      trackUpgradeButtonClicked('subscribe_page');
      trackButtonClick('proceed_to_checkout', {
        plan: selectedPlan,
        amount: checkoutData.amount,
        currency: checkoutData.currency,
      });

      // Meta Pixel: Track InitiateCheckout for ads attribution
      trackCustom('InitiateCheckout', {
        value: checkoutData.amount,
        currency: checkoutData.currency,
        content_name: selectedPlan === 'year' ? 'Annual Subscription' : 'Monthly Subscription',
      });

      const response = await subscriptionService.createCheckoutSession(checkoutData);
      if (!response || !response.checkout_url) {
        throw new Error('Invalid checkout response from server');
      }
      window.location.href = response.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      // Extract error message from various possible response structures
      let errorMsg = 'Payment failed. Please try again.';

      if (err.response?.data?.detail?.message) {
        errorMsg = err.response.data.detail.message;
      } else if (err.response?.data?.error?.message) {
        errorMsg = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.response?.data?.detail) {
        // Handle array of errors
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail[0]?.msg || errorMsg;
        } else {
          errorMsg = err.response.data.detail;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }

      // Track checkout error
      trackError('checkout_failed', errorMsg, {
        plan: selectedPlan,
        amount: selectedPlan === 'year' ? SUBSCRIPTION_PRICE_YEAR : SUBSCRIPTION_PRICE_MONTH,
      });

      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    // If user is not authenticated, show signup form instead of proceeding to payment
    if (!user) {
      setShowSignupForm(true);
      return;
    }

    // If user is authenticated, proceed to payment directly
    await proceedToPayment();
  };

  if (hasActiveSubscription && isPaidSubscription) {
    return (
      <div className="subscribe-page">
        <Helmet>
          <title>Subscription - Anas Arabic Learning</title>
        </Helmet>
        <InternalNavbar />

        <div className="subscribe-hero-section">
          <div className="container">
            <div className="subscribe-hero-content">
              <div className="crown-icon">
                <FaCrown />
              </div>
              <h1>You're Already a Premium Member!</h1>
              <p>Your subscription is active until {new Date(subscription.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </div>

        <div className="subscribe-content-section">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6">
                <div className="already-subscribed-card">
                  <FaCheck className="check-icon" />
                  <h2>Continue Your Learning Journey</h2>
                  <p>Access your dashboard to continue learning Arabic with full premium features.</p>
                  <button onClick={() => navigate('/dashboard')} className="btn-dashboard">
                    Go to Dashboard <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
        <Scrollbar />
      </div>
    );
  }

  return (
    <div className="subscribe-page">
      <Helmet>
        <title>Subscribe - Anas Arabic Learning</title>
        <meta name="description" content="Subscribe to Anas Arabic Learning. 12 months of full access + 2 free months for only $40." />
      </Helmet>
      <InternalNavbar />

      {/* Main Conversion Section */}
      <div className="subscribe-conversion-section">
        <div className="container">

          {/* Hero Title */}
          <div className="conversion-hero-title">
            <h1>Thank you for choosing Anas!</h1>
            <h2>Unlock Instant Access to our Platform</h2>
          </div>

          {error && !showSignupForm && (
            <div className="alert alert-warning subscribe-alert">
              <FaCheck /> {error}
            </div>
          )}

          {/* Horizontal Layout */}
          <div className="row align-items-center conversion-row">

            {/* Left Side - Icon Display */}
            <div className="col-lg-5">
              <div className="conversion-icon-display">
                <div className="icon-circle">
                  <FaCrown />
                </div>
                <h3>Premium Access</h3>
                <p>Unlock all features and content</p>
              </div>
            </div>

            {/* Right Side - Content */}
            <div className="col-lg-7">
              <div className="conversion-content">
                {showSignupForm ? (
                  // Simplified enrollment form for unauthenticated users
                  <div className="simplified-enroll-form">
                    <h3>Create Your Account</h3>
                    <p>Enter your details to create an account and proceed to payment</p>

                    {error && (
                      <div className="alert alert-warning subscribe-alert">
                        <FaCheck /> {error}
                      </div>
                    )}

                    <form onSubmit={handleSimplifiedEnroll}>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="first_name">First Name *</label>
                            <div className="input-with-icon">
                              <FaUser />
                              <input
                                type="text"
                                id="first_name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                placeholder="Enter your first name"
                                disabled={loading}
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="last_name">Last Name *</label>
                            <div className="input-with-icon">
                              <FaUser />
                              <input
                                type="text"
                                id="last_name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                placeholder="Enter your last name"
                                disabled={loading}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <div className="input-with-icon">
                          <FaEnvelope />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter your email address"
                            disabled={loading}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <div className="input-with-icon">
                          <FaKey />
                          <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Create a password"
                            disabled={loading}
                            required
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="btn-proceed-payment"
                        disabled={loading}
                      >
                        {loading ? 'Creating Account...' : (
                          <>
                            Create Account & Proceed <FaArrowRight />
                          </>
                        )}
                      </button>
                    </form>

                    <div className="terms-notice">
                      <p>
                        By creating an account, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>.
                      </p>
                    </div>
                  </div>
                ) : (
                  // Existing content for all users (authenticated and unauthenticated)
                  <>
                    {/* Welcome Message */}
                    <div className="welcome-message">
                      <p>We are pleased to have you among our learners!</p>
                    </div>

                    {/* Plan Selection */}
                    <div className="plan-selection">
                      <h3>Choose Your Plan</h3>
                      <div className="plan-toggle-container">
                        <div
                          className={`plan-option ${selectedPlan === 'month' ? 'active' : ''}`}
                          onClick={() => setSelectedPlan('month')}
                        >
                          <div className="plan-radio">
                            <div className="radio-circle"></div>
                          </div>
                          <div className="plan-details">
                            <span className="plan-name">Monthly</span>
                            <span className="plan-price">${SUBSCRIPTION_PRICE_MONTH} / month</span>
                          </div>
                        </div>

                        <div
                          className={`plan-option ${selectedPlan === 'year' ? 'active' : ''}`}
                          onClick={() => setSelectedPlan('year')}
                        >
                          <div className="plan-radio">
                            <div className="radio-circle"></div>
                          </div>
                          <div className="plan-details">
                            <span className="plan-name">Yearly</span>
                            <span className="plan-price">${SUBSCRIPTION_PRICE_YEAR} / year</span>
                            <span className="plan-badge">2 Months Free</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Value Proposition */}
                    <div className="value-proposition">
                      {selectedPlan === 'year' ? (
                        <p>By subscribing yearly, you'll gain access to <strong>Anas Arabic Learning</strong> for <strong>12 Months at the price of 10!</strong></p>
                      ) : (
                        <p>Flexible monthly access to <strong>Anas Arabic Learning</strong>. Cancel anytime.</p>
                      )}
                    </div>

                    {/* Pricing Display */}
                    <div className="pricing-comparison">
                      {selectedPlan === 'year' && (
                        <div className="price-row">
                          <span className="price-label">Old Price:</span>
                          <span className="old-price-amount">
                            ${OLD_PRICE_YEAR}
                          </span>
                        </div>
                      )}
                      <div className="price-row featured">
                        <span className="price-label">New Price:</span>
                        <span className="new-price-amount">
                          ${selectedPlan === 'year' ? SUBSCRIPTION_PRICE_YEAR : SUBSCRIPTION_PRICE_MONTH}
                        </span>
                      </div>
                    </div>

                    {/* CTA Message */}
                    <div className="cta-message">
                      <h3>Start Learning Arabic Today with Anas!</h3>
                    </div>

                    {/* CTA Button */}
                    <button
                      className="btn-proceed-payment"
                      onClick={handleSubscribe}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : (
                        <>
                          Proceed to Payment <FaArrowRight />
                        </>
                      )}
                    </button>

                    {/* Security Badge */}
                    <div className="security-badge">
                      <FaLock /> Secure payment via Stripe
                    </div>

                    {user && (
                      <div className="user-info-minimal">
                        <p>Logged in as: <strong>{user?.email}</strong></p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
      <Scrollbar />
    </div>
  );
};

export default SubscribePage;